import * as React from "react";
import clsx from "clsx";

export type BadgeSize = "1" | "2" | "3";
export type BadgeVariant = "solid" | "soft" | "surface" | "outline";
export type BadgeRadius = "none" | "small" | "medium" | "large" | "full";
export type BadgeColor = "success" | "warning" | "danger";

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "color"> {
    /** Controls the size of the badge */
    size?: BadgeSize;
    /** Determines the visual style */
    variant?: BadgeVariant;
    /** Specifies the badge's color */
    color?: BadgeColor;
    /** If true, increases color contrast with the background */
    highContrast?: boolean;
    /** Sets the border radius */
    radius?: BadgeRadius;
    /** Badge content */
    children?: React.ReactNode;
}

const SIZE_CLASSES: Record<BadgeSize, string> = {
    "1": "text-xs px-1.5 py-0.5",
    "2": "text-sm px-2 py-1",
    "3": "text-base px-2.5 py-1.5"
};

const RADIUS_CLASSES: Record<BadgeRadius, string> = {
    none: "rounded-none",
    small: "rounded-sm",
    medium: "rounded",
    large: "rounded-lg",
    full: "rounded-full"
};

// Color mappings for different variants using standard Tailwind colors
// Maps radix-ui color tokens to Tailwind color classes
// Using explicit class names so Tailwind JIT can detect them
const getVariantColorClasses = (variant: BadgeVariant, color: BadgeColor, highContrast: boolean): string => {
    const colorMap: Record<BadgeColor, Record<BadgeVariant, { normal: string; highContrast: string }>> = {
        success: {
            solid: { normal: "bg-green-500 text-white", highContrast: "bg-green-600 text-white" },
            soft: { normal: "bg-green-100 text-green-700", highContrast: "bg-green-200 text-green-900" },
            surface: {
                normal: "bg-green-50 text-green-700 border border-green-200",
                highContrast: "bg-green-100 text-green-900 border border-green-300"
            },
            outline: {
                normal: "border border-green-400 text-green-700 bg-transparent",
                highContrast: "border border-green-600 text-green-900 bg-transparent"
            }
        },
        warning: {
            solid: { normal: "bg-yellow-500 text-white", highContrast: "bg-yellow-700 text-white" },
            soft: { normal: "bg-yellow-100 text-yellow-700", highContrast: "bg-yellow-200 text-yellow-900" },
            surface: {
                normal: "bg-yellow-50 text-yellow-700 border border-yellow-200",
                highContrast: "bg-yellow-100 text-yellow-900 border border-yellow-300"
            },
            outline: {
                normal: "border border-yellow-400 text-yellow-700 bg-transparent",
                highContrast: "border border-yellow-600 text-yellow-900 bg-transparent"
            }
        },
        danger: {
            solid: { normal: "bg-red-500 text-white", highContrast: "bg-red-700 text-white" },
            soft: { normal: "bg-red-100 text-red-700", highContrast: "bg-red-200 text-red-900" },
            surface: {
                normal: "bg-red-50 text-red-700 border border-red-200",
                highContrast: "bg-red-100 text-red-900 border border-red-300"
            },
            outline: {
                normal: "border border-red-400 text-red-700 bg-transparent",
                highContrast: "border border-red-600 text-red-900 bg-transparent"
            }
        }
    };

    const colorVariant = colorMap[color] || colorMap.success;
    const variantStyle = colorVariant[variant];
    return highContrast ? variantStyle.highContrast : variantStyle.normal;
};

/**
 * Badge component matching radix-ui/themes Badge interface
 * - Supports size variants: "1", "2", "3"
 * - Supports style variants: "solid", "soft", "surface", "outline"
 * - Supports color tokens matching radix-ui themes
 * - Supports high contrast mode
 * - Supports radius variants: "none", "small", "medium", "large", "full"
 * - Supports asChild prop for composition
 */
export function Badge(props: BadgeProps) {
    const {
        size = "1",
        variant = "soft",
        color = "success",
        highContrast = false,
        radius = "medium",
        className,
        children,
        style,
        ...rest
    } = props;

    const baseClasses = "inline-flex items-center justify-center font-medium leading-none whitespace-nowrap";
    const sizeClasses = SIZE_CLASSES[size];
    const radiusClasses = RADIUS_CLASSES[radius];
    const variantColorClasses = getVariantColorClasses(variant, color, highContrast);

    const badgeClasses = clsx(baseClasses, sizeClasses, radiusClasses, variantColorClasses, className);

    return (
        <span className={badgeClasses} style={style} {...rest}>
            {children}
        </span>
    );
}
